/**
 * Pure shaping for "In the House" history (Reports > Check-ins, the tile's
 * today curve, Competitors). The RPC (`get_in_house_report`, migration
 * 20261016100000) returns, per Bali day, the stored quarter-hour counts from
 * 06:00 to 21:45 (22:00 onward is zero by rule) with Reload's occupancy at the
 * same mark. Everything here is averaging and picking, kept out of the
 * components so it can be tested.
 */
import { addDaysToBaliKey } from '@/lib';

export interface InHousePoint {
  /** Bali wall-clock "HH:MM" of the quarter-hour mark. */
  time: string;
  total: number;
  with_key: number;
  by_timer: number;
  /** Reload Sanctuary's occupancy reading at the same mark; null = no reading. */
  reload: number | null;
}

export interface InHouseDay {
  date: string;
  /** Share of the day's checked-in members who got a locker key (0–1). */
  key_share: number | null;
  peak: { time: string; total: number } | null;
  points: InHousePoint[];
}

export interface InHouseReport {
  days: InHouseDay[];
}

/** Below this key share, too few keys were logged and the timer dominates. */
export const LOW_KEY_SHARE = 0.35;

/** The 64 quarter-hour marks the report covers: 06:00 … 21:45. */
export const IN_HOUSE_MARKS: string[] = Array.from({ length: 64 }, (_, i) => {
  const minutes = 6 * 60 + i * 15;
  return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
});

/** Hours shown in the weekday × hour grid: HOK is open 07:00–22:00. */
export const IN_HOUSE_GRID_HOURS = Array.from({ length: 15 }, (_, i) => i + 7);

const round1 = (value: number) => Math.round(value * 10) / 10;

function mean(values: Array<number | null | undefined>): number | null {
  const present = values.filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
  if (present.length === 0) return null;
  return round1(present.reduce((sum, v) => sum + v, 0) / present.length);
}

/** Days with at least one stored mark. */
export function daysWithData(days: InHouseDay[]): InHouseDay[] {
  return days.filter((d) => d.points.length > 0);
}

export interface InHouseCurvePoint {
  time: string;
  /** Average in the House at this mark over the days (the day's own value for one day). */
  total: number | null;
  withKey: number | null;
  byTimer: number | null;
  /** Highest count at this mark over the days. */
  max: number | null;
  reload: number | null;
}

/** One point per mark, averaged over the given days. Marks no day reached stay null. */
export function inHouseCurve(days: InHouseDay[]): InHouseCurvePoint[] {
  const byDay = days.map((d) => new Map(d.points.map((p) => [p.time, p])));
  return IN_HOUSE_MARKS.map((time) => {
    const at = byDay.map((m) => m.get(time)).filter((p): p is InHousePoint => p !== undefined);
    return {
      time,
      total: mean(at.map((p) => p.total)),
      withKey: mean(at.map((p) => p.with_key)),
      byTimer: mean(at.map((p) => p.by_timer)),
      max: at.length ? Math.max(...at.map((p) => p.total)) : null,
      reload: mean(at.map((p) => p.reload)),
    };
  });
}

export interface BusiestMark {
  time: string;
  value: number;
}

/** The mark with the highest average; ties go to the earlier mark. */
export function busiestMark(curve: InHouseCurvePoint[]): BusiestMark | null {
  let best: BusiestMark | null = null;
  for (const p of curve) {
    if (p.total !== null && (best === null || p.total > best.value)) best = { time: p.time, value: p.total };
  }
  return best;
}

export interface Headcount {
  date: string;
  time: string;
  total: number;
}

/** The single highest count over the days, with when it happened. */
export function highestHeadcount(days: InHouseDay[]): Headcount | null {
  let best: Headcount | null = null;
  for (const d of days) {
    if (d.peak && (best === null || d.peak.total > best.total)) {
      best = { date: d.date, time: d.peak.time, total: d.peak.total };
    }
  }
  return best;
}

/** Average of each day's own peak. */
export function averageDailyPeak(days: InHouseDay[]): number | null {
  return mean(days.map((d) => d.peak?.total));
}

export function lowKeyDays(days: InHouseDay[]): InHouseDay[] {
  return days.filter((d) => d.key_share !== null && d.key_share < LOW_KEY_SHARE);
}

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

/** Monday = 0 for a Bali date key (the key is a calendar date, so UTC is exact). */
export function weekdayIndex(dateKey: string): number {
  return (new Date(`${dateKey}T00:00:00Z`).getUTCDay() + 6) % 7;
}

export interface WeekdayRow {
  weekday: (typeof WEEKDAYS)[number];
  days: number;
  /** Average in the House per hour (mean of the hour's four marks), by IN_HOUSE_GRID_HOURS. */
  cells: Array<number | null>;
}

/** Weekday × hour averages: "how full is the House on a Tuesday at 18:00". */
export function weekdayHourGrid(days: InHouseDay[]): WeekdayRow[] {
  return WEEKDAYS.map((weekday, index) => {
    const own = days.filter((d) => weekdayIndex(d.date) === index && d.points.length > 0);
    const cells = IN_HOUSE_GRID_HOURS.map((hour) => {
      const prefix = `${String(hour).padStart(2, '0')}:`;
      return mean(own.map((d) => mean(d.points.filter((p) => p.time.startsWith(prefix)).map((p) => p.total))));
    });
    return { weekday, days: own.length, cells };
  });
}

export interface TodayCurvePoint {
  time: string;
  today: number | null;
  usual: number | null;
}

/**
 * The tile's small chart: today's marks so far against the average of the
 * same weekday over the previous four weeks.
 */
export function todayVersusUsual(days: InHouseDay[], todayKey: string): TodayCurvePoint[] {
  const today = days.find((d) => d.date === todayKey);
  const sameWeekday = [7, 14, 21, 28]
    .map((n) => days.find((d) => d.date === addDaysToBaliKey(todayKey, -n)))
    .filter((d): d is InHouseDay => d !== undefined && d.points.length > 0);
  const todayByTime = new Map((today?.points ?? []).map((p) => [p.time, p.total]));
  const usual = inHouseCurve(sameWeekday);
  return IN_HOUSE_MARKS.filter((t) => t >= '07:00').map((time) => ({
    time,
    today: todayByTime.get(time) ?? null,
    usual: usual.find((u) => u.time === time)?.total ?? null,
  }));
}
